/**
 * UIResolvers/tableEditor.js, TableEditor/index.js
 */
import React, { useCallback, useEffect, useState } from "react";
import { useTable, usePagination } from "react-table";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useFieldArray } from "react-final-form-arrays";
import { useRecoilState } from "recoil";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { MdFullscreen } from "react-icons/md";
import { AngleDoubleLeft } from "@styled-icons/fa-solid/AngleDoubleLeft";
import { AngleDoubleRight } from "@styled-icons/fa-solid/AngleDoubleRight";
import { AngleLeft } from "@styled-icons/fa-solid/AngleLeft";
import { AngleRight } from "@styled-icons/fa-solid/AngleRight";
import { tablePageState } from "../../SimpleForm/Atoms/Table/state";
import {
  removeRowFromTableErrors,
  buildAllTableErrors,
} from "../../SimpleForm/Atoms/Table/utils";
import { validator } from "../../SimpleForm/validation";
import {
  Styles,
  TH,
  TD,
  PageInput,
  PageNavButton,
  Pagination,
} from "../../SimpleForm/UI/styles/Table";
import worksiteColumnsH2B from "../Columns/worksiteColumnsH2B";
import worksiteColumnsCW1 from "../Columns/worksiteColumnsCW1";
import worksiteColumnsPERM from "../Columns/worksiteColumnsPERM";
import "./styles.scss";
import "react-day-picker/lib/style.css";

const tableEditorUiResolver = (key) => {
  switch (key) {
    case "formUI9142B_employmentLocations":
      return worksiteColumnsH2B;
    case "formUI9142C_appx_b":
      return worksiteColumnsCW1;
    case "formUI9089_appx_b":
      return worksiteColumnsPERM;
    default:
      return new Error("Invalid resolver key passed in.");
  }
};

const getFormData = (formApi, fields) => {
  if (!fields || !formApi) {
    return {};
  }
  let obj = {};
  const formState = (formApi.getState() && formApi.getState().values) || {};
  fields.forEach((field) => {
    obj[`${field}`] = formState[field] || null;
  });
  return obj;
};

const getEditableMachineName = (form_ui_name) => {
  const formType = form_ui_name.slice(6);
  if (formType === "9142B") return "employmentLocations";
  if (formType === "9142C" || formType === "9089") return "appx_b";
};

// table editor opts into using Recoil and local state per cell since tons of Field subscriptions is a bottleneck
// https://github.com/final-form/react-final-form/issues/336

const Table = ({ data, schemaProps, formApi, onDragEnd, ...rest }) => {
  const { form_ui_name, formValues, tablePageIndex, setTablePageIndex } = rest;
  const { machine_name, validate, fromFields } = schemaProps;
  // machine_name and form_ui_name should get data and columns
  const columnsKey = `${form_ui_name}_${machine_name}`;
  const columns = tableEditorUiResolver(columnsKey);
  const formData = getFormData(formApi, fromFields);
  const manualPagination = false;
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      manualPagination,
      initialState: { pageIndex: tablePageIndex },
    },
    usePagination
  );
  const [deletedRow, setDeletedRow] = useState();
  const [full, setFull] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const { ...machineNameArray } = useFieldArray(`${machine_name}`);
  const editable_machine = getEditableMachineName(form_ui_name);
  const deletedRows = removeRowFromTableErrors(deletedRow, machine_name);
  const processTableAllErrors = buildAllTableErrors(
    data,
    editable_machine,
    formData
  );

  // check if table level errors exist when row count changes
  validator(validate, formValues)(data);

  // Here in case we need to add custom styling for rows being dragged over for react-beautiful-dnd
  const getListStyle = (isDraggingOver) => ({
    background: isDraggingOver ? "#005ea2" : "lightgrey",
  });

  const handle = useFullScreenHandle();

  const fullScreenChange = useCallback((state, handle) => {
    if (state === true) setFull(true);
    else setFull(false);
  }, []);

  return (
    !!data &&
    !!data.length && (
      <>
        {deletedRows}
        {processTableAllErrors}
        <div
          onClick={handle.enter}
          onKeyUp={(e) => {
            if (e.key === " ") {
              handle.enter(e);
            }
          }}
          className="fullScreenIconContainer"
        >
          <MdFullscreen className="fullScreenIcon" size={50} />
          <span className="tooltiptext">Full Screen Mode</span>
        </div>
        <FullScreen handle={handle} onChange={fullScreenChange}>
          <Styles>
            <DragDropContext onDragEnd={onDragEnd}>
              <div
                className={!(full || isDragging) && "worksite-table-container"}
              >
                <table {...getTableProps()}>
                  <thead>
                    {headerGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <TH
                            {...column.getHeaderProps()}
                            {...column.getHeaderProps({
                              style: {
                                minWidth: column.minWidth,
                                width: column.width,
                              },
                            })}
                          >
                            {column.render("Header")}
                          </TH>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <Droppable droppableId="droppable">
                    {(provided, snapshot) => (
                      <tbody
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={getListStyle(snapshot.isDraggingOver)}
                        {...getTableBodyProps()}
                      >
                        {page.map((row, i) => {
                          prepareRow(row);
                          const {
                            id,
                            original: { rowNum },
                          } = row;
                          let key = `${id}_${i}_${rowNum}`;
                          setIsDragging(snapshot.isDraggingOver);
                          return (
                            <Row
                              keyValue={key}
                              row={row}
                              formApi={formApi}
                              formData={formData}
                              machineName={machine_name}
                              setDeletedRow={setDeletedRow}
                              machineNameArray={machineNameArray}
                            />
                          );
                        })}
                      </tbody>
                    )}
                  </Droppable>
                </table>
              </div>
            </DragDropContext>
            <p>
              <i>
                The filer may drag and drop a row to reorder the information.
              </i>
            </p>
            <br />
            <Pagination left={true}>
              <PageNavButton
                onClick={(e) => {
                  e.preventDefault();
                  const first_page = manualPagination ? 1 : 0;
                  gotoPage(first_page);
                  setTablePageIndex(1);
                }}
                title="First Page"
                disabled={
                  (pageIndex === 1 && manualPagination) || !canPreviousPage
                }
              >
                <AngleDoubleLeft size="1rem" />
              </PageNavButton>{" "}
              <PageNavButton
                onClick={(e) => {
                  e.preventDefault();
                  previousPage();
                  setTablePageIndex(pageIndex - 1);
                }}
                title="Previous Page"
                disabled={
                  (pageIndex === 1 && manualPagination) || !canPreviousPage
                }
              >
                <AngleLeft size="1rem" />
              </PageNavButton>{" "}
              <div className="input-page">
                <label htmlFor="Pagination">Page </label>
                <span>
                  <PageInput
                    type="number"
                    title="Page Number"
                    value={pageIndex + 1}
                    onChange={(e) => {
                      if (e.target.value === "") {
                      } else {
                        const first_page = manualPagination ? 1 : 0;
                        const page = e.target.value
                          ? manualPagination
                            ? Number(e.target.value)
                            : Number(e.target.value) - 1
                          : first_page;
                        gotoPage(page);
                        setTablePageIndex(page);
                      }
                    }}
                  />
                </span>{" "}
                of{" "}
                {manualPagination ? pageOptions.length - 1 : pageOptions.length}
              </div>
              <PageNavButton
                onClick={(e) => {
                  e.preventDefault();
                  nextPage();
                  setTablePageIndex(pageIndex + 1);
                }}
                title="Next Page"
                disabled={!canNextPage}
              >
                <AngleRight size="1rem" />
              </PageNavButton>{" "}
              <PageNavButton
                onClick={(e) => {
                  e.preventDefault();
                  gotoPage(pageOptions.length - 1);
                  setTablePageIndex(pageOptions.length - 1);
                }}
                title="Last Page"
                disabled={!canNextPage}
              >
                <AngleDoubleRight size="1rem" />
              </PageNavButton>{" "}
              <select
                value={pageSize}
                title="Page Size"
                onChange={(e) => {
                  const first_page = manualPagination ? 1 : 0;
                  setPageSize(Number(e.target.value));
                  gotoPage(first_page);
                  setTablePageIndex(first_page);
                }}
              >
                {[10, 25, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    Show {pageSize}
                  </option>
                ))}
              </select>
            </Pagination>
          </Styles>
        </FullScreen>
      </>
    )
  );
};
const Row = ({
  keyValue,
  row,
  formApi,
  formData,
  machineName,
  machineNameArray,
  setDeletedRow,
}) => {
  let rowIndex = row.index;
  const { ...fieldArray } = useFieldArray(`${machineName}[${rowIndex}]`, {
    subscription: { value: true },
  });

  const getItemStyle = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    // userSelect: "none",
    // padding: 16,
    // opacity: 1,

    // change background colour if dragging
    background: isDragging ? "rgb(227 226 226)" : "white",

    // styles we need to apply on draggables
    ...draggableStyle,
  });

  return (
    <Draggable
      key={`row-${rowIndex}`}
      draggableId={`row-${rowIndex}`}
      index={rowIndex}
    >
      {(provided, snapshot) => (
        <tr
          key={keyValue}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          {...row.getRowProps()}
          style={getItemStyle(
            snapshot.isDragging,
            provided.draggableProps.style
          )}
        >
          {row.cells.map((cell) => {
            return (
              <TD
                {...cell.getCellProps()}
                {...cell.getCellProps({
                  style: { overflow: cell.column.overflow },
                })}
              >
                {cell.render("Cell", {
                  machineName,
                  fieldArray,
                  machineNameArray,
                  formApi,
                  formData,
                  setDeletedRow,
                })}
              </TD>
            );
          })}
        </tr>
      )}
    </Draggable>
  );
};

const TableEditor = (props) => {
  const {
    formApi,
    form_ui_name,
    schemaProps: { machine_name },
  } = props;
  const [tablePageIndex, setTablePageIndex] = useState(0);
  const [reordered, setReordered] = useState(null);
  const data = formApi.getState().values?.[machine_name] ?? [];

  useEffect(() => {
    if (!!reordered?.length) {
      formApi.change(getEditableMachineName(form_ui_name), reordered);
    }
  }, [reordered]);

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items = reorder(data, result.source.index, result.destination.index);
    // state hook called and use effect called as a result
    setReordered(items);
  };

  return (
    <Table
      {...props}
      data={data}
      tablePageIndex={tablePageIndex}
      setTablePageIndex={setTablePageIndex}
      onDragEnd={onDragEnd}
    />
  );
};

export default TableEditor;
